import { Button, Card, Tab, Tabs } from '@airbus/components-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNotificationContext } from '../context/NotificationContext';
import { useUserContextApi } from '../context/UserContext';
import { useLanguageContextAPI } from '../context/LanguageContext';
import styles from './NotificationTabsPanel.module.css';
import Spinners from './Spinner';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import { postRequest } from '../utils';
import fetchUserNotifications from '../services/notification/client/fetchNotifications';
import NewNotificationCard, {
  NewNotificationAction,
  NewNotificationItem,
} from './notification/NewNotificationCard';
import {
  getCountsAfterRemoval,
  getLocalizedViewManagerLabel,
  getViewShareNotificationCreatorName,
  moveGeneralNotificationToUnreadTop,
  reorderLoadedGeneralNotifications,
} from './notification/notificationTabsPanelHelpers';
import type {
  NotificationStatus,
} from '../types';
import { resolveNotificationAction } from '../services/notification/workflow';
import { updateNotificationStatus as updateNewNotificationStatus } from '../services/notification/client/updateNotification';
import { markAllNotificationsAsRead } from '../services/notification/client/markAllAsRead';
import fetchViewManagerNotifications from '../services/notification/client/fetchViewManagerNotifications';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

type LoadingControls = {
  showInitialLoader: boolean;
  startInitialLoad: () => void;
  stopInitialLoad: () => void;
  startBackgroundLoad: () => void;
  stopBackgroundLoad: () => void;
};

type ShowMoreControlProps = {
  visible: boolean;
  loading: boolean;
  disabled: boolean;
  remainingCount: number;
  onClick: () => void;
};

type LoadGeneralNotificationsOptions = {
  offset?: number;
  limit?: number;
  append?: boolean;
  showInitialLoader?: boolean;
};

const VIEW_MANAGER_PAGE_SIZE = 20;
const GENERAL_PAGE_SIZE = 20;

// Wraps notification fetch calls with consistent initial/background loading state toggles.
async function runNotificationLoad<T>({
  showInitialLoader,
  startInitialLoad,
  stopInitialLoad,
  startBackgroundLoad,
  stopBackgroundLoad,
  load,
}: LoadingControls & {
  load: () => Promise<T>;
}): Promise<T | undefined> {
  if (showInitialLoader) {
    startInitialLoad();
  } else {
    startBackgroundLoad();
  }

  try {
    return await load();
  } finally {
    if (showInitialLoader) {
      stopInitialLoad();
    } else {
      stopBackgroundLoad();
    }
  }
}

function mergeGeneralNotifications(
  prev: NewNotificationItem[],
  rows: NewNotificationItem[],
  append: boolean,
) {
  if (!append) {
    return reorderLoadedGeneralNotifications(rows);
  }

  const seen = new Set(prev.map((item) => item.userNotificationId));
  const nextRows = rows.filter((item) => !seen.has(item.userNotificationId));
  return reorderLoadedGeneralNotifications([...prev, ...nextRows]);
}

// Renders the shared Show more action used by both notification tabs.
function ShowMoreControl({
  visible,
  loading,
  disabled,
  remainingCount,
  onClick,
}: ShowMoreControlProps) {
  if (!visible) {
    return null;
  }

  return (
    <div className={styles.showMoreRow}>
      <button
        type='button'
        className={styles.showMoreButton}
        disabled={disabled}
        onClick={onClick}
      >
        {loading ? (
          'Loading...'
        ) : (
          <span className={styles.showMoreLabel}>
            {`Show more (${remainingCount})`}
            <KeyboardArrowDownIcon className={styles.showMoreIcon} />
          </span>
        )}
      </button>
    </div>
  );
}

// TODO: Move this component to src/shared/components/notification/ and split tab-specific logic into separate components (for example, View Manager tab) in a follow-up refactor.
export default function NotificationTabsPanel() {
  const {
    notifications,
    addNotifications,
    isNotificationPanelOpen,
    notificationFetchingStarted,
    setNotificationFetchingStarted,
    setExternalNotificationCount,
    clearAllNotifications,
    updateNotificationFields,
    setProcessedNotificationId,
    removeNotification,
  } = useNotificationContext();
  const { t, language } = useLanguageContextAPI();

  const { userDetails } = useUserContextApi();
  const [selectedTab, setSelectedTab] = useState(0);
  const [clearAllVisible] = useState(false);
  const [newNotifications, setNewNotifications] = useState<NewNotificationItem[]>([]);
  const [newNotificationsLoading, setNewNotificationsLoading] = useState(false);
  const [generalPageState, setGeneralPageState] = useState({
    offset: 0,
    hasMore: false,
    loadingMore: false,
    totalCount: 0,
    loadedCount: 0,
  });
  const [viewManagerPageState, setViewManagerPageState] = useState({
    loadingMore: false,
    totalCount: 0,
    loadedCount: 0,
  });
  const [processingNewNotificationIds, setProcessingNewNotificationIds] = useState<
    Record<string, boolean>
  >({});
  const processingNewNotificationIdSet = useRef<Set<string>>(new Set());

  const generalRemainingCount = useMemo(
    () => Math.max(generalPageState.totalCount - generalPageState.loadedCount, 0),
    [generalPageState.loadedCount, generalPageState.totalCount],
  );

  const viewManagerRemainingCount = useMemo(
    () => Math.max(viewManagerPageState.totalCount - viewManagerPageState.loadedCount, 0),
    [viewManagerPageState.loadedCount, viewManagerPageState.totalCount],
  );

  const viewManagerHasMore = useMemo(
    () => viewManagerRemainingCount > 0,
    [viewManagerRemainingCount],
  );

  // Resets General tab data and paging state when user context changes.
  const resetGeneralNotificationsState = () => {
    setNewNotifications([]);
    setGeneralPageState({
      offset: 0,
      hasMore: false,
      loadingMore: false,
      totalCount: 0,
      loadedCount: 0,
    });
  };

  // Loads View Manager notifications with optional full-panel loading state.
  async function loadViewManagerNotifications({
    limit,
    showInitialLoader = false,
  }: {
    limit: number;
    showInitialLoader?: boolean;
  }) {
    if (!userDetails?.userId) return;

    try {
      await runNotificationLoad({
        showInitialLoader,
        startInitialLoad: () => setNotificationFetchingStarted(true),
        stopInitialLoad: () => setNotificationFetchingStarted(false),
        startBackgroundLoad: () => setViewManagerPageState((prev) => ({ ...prev, loadingMore: true })),
        stopBackgroundLoad: () => setViewManagerPageState((prev) => ({ ...prev, loadingMore: false })),
        load: async () => {
          const data = await fetchViewManagerNotifications(Number(userDetails.userId), limit, 0);

          if (!data) {
            console.error('Failed to fetch View Manager notifications');
            return;
          }

          const rows = data.notifications ?? [];
          const mappedNotifications = rows.map((item) => {
            return {
              notificationId: String(item.id),
              label: getLocalizedViewManagerLabel({ value: item }, t, language),
              readStatus: item.readNotification,
              date: item.insert_date ?? undefined,
              value: item,
            };
          });

          addNotifications(mappedNotifications);
          setViewManagerPageState((prev) => ({
            ...prev,
            loadedCount: mappedNotifications.length,
            totalCount: data.totalCount ?? mappedNotifications.length,
          }));
        },
      });
    } catch (error) {
      console.error('Failed to load View Manager notifications', error);
      return;
    }
  }

  // Loads General notifications with pagination and deduplicated append behavior.
  async function loadGeneralNotifications({
    offset = 0,
    limit = GENERAL_PAGE_SIZE,
    append = false,
    showInitialLoader = true,
  }: LoadGeneralNotificationsOptions) {
    if (!userDetails?.userId) return;

    try {
      await runNotificationLoad({
        showInitialLoader,
        startInitialLoad: () => setNewNotificationsLoading(true),
        stopInitialLoad: () => setNewNotificationsLoading(false),
        startBackgroundLoad: () => setGeneralPageState((prev) => ({ ...prev, loadingMore: true })),
        stopBackgroundLoad: () => setGeneralPageState((prev) => ({ ...prev, loadingMore: false })),
        load: async () => {
          const userId = Number(userDetails.userId);
          const notificationData = await fetchUserNotifications(userId, limit, offset);
          const rows = notificationData?.notifications ?? [];

          setNewNotifications((prev) => mergeGeneralNotifications(prev, rows, append));

          setGeneralPageState((prev) => ({
            ...prev,
            totalCount: notificationData?.totalCount ?? 0,
            loadedCount: append ? prev.loadedCount + rows.length : rows.length,
            hasMore: Boolean(notificationData?.hasMore),
            offset: notificationData?.nextOffset ?? offset + rows.length,
          }));
        },
      });
    } catch (error) {
      console.error('Failed to load General notifications', error);
    }
  }

  // Fills one General notification slot after a remove when more items exist on the server.
  const topUpGeneralAfterRemoval = async (
    nextLoadedCount: number,
    nextTotalCount: number,
    offset: number,
  ) => {
    if (nextTotalCount <= nextLoadedCount) {
      return;
    }

    await loadGeneralNotifications({
      offset,
      limit: 1,
      append: true,
      showInitialLoader: false,
    });
  };

  // Removes one General notification locally and updates counters before optional top-up.
  const removeGeneralNotificationAndTopUp = (userNotificationId: number) => {
    setNewNotifications((prev) =>
      prev.filter((item) => item.userNotificationId !== userNotificationId),
    );

    const nextLoadedCount = Math.max(generalPageState.loadedCount - 1, 0);
    const nextTotalCount = Math.max(generalPageState.totalCount - 1, 0);
    const nextOffset = generalPageState.offset;

    setGeneralPageState((prev) => ({
      ...prev,
      loadedCount: Math.max(prev.loadedCount - 1, 0),
      totalCount: Math.max(prev.totalCount - 1, 0),
      hasMore: Math.max(prev.totalCount - 1, 0) > Math.max(prev.loadedCount - 1, 0),
    }));

    void topUpGeneralAfterRemoval(nextLoadedCount, nextTotalCount, nextOffset);
  };

  // Preloads both tabs in the background so header badge count is available on page load.
  useEffect(() => {
    if (!userDetails?.userId) {
      setExternalNotificationCount(0);
      return;
    }

    void loadGeneralNotifications({
      offset: 0,
      append: false,
      showInitialLoader: false,
    });

    void loadViewManagerNotifications({
      limit: VIEW_MANAGER_PAGE_SIZE,
      showInitialLoader: false,
    });
  }, [userDetails?.userId]);

  useEffect(() => {
    if (!userDetails?.userId) {
      resetGeneralNotificationsState();
      setViewManagerPageState({ loadingMore: false, totalCount: 0, loadedCount: 0 });
      return;
    }

    if (!isNotificationPanelOpen) {
      return;
    }

    resetGeneralNotificationsState();
    setViewManagerPageState({ loadingMore: false, totalCount: 0, loadedCount: 0 });
    void loadGeneralNotifications({ offset: 0, append: false, showInitialLoader: true });
    void loadViewManagerNotifications({ limit: VIEW_MANAGER_PAGE_SIZE, showInitialLoader: false });
  }, [userDetails?.userId, isNotificationPanelOpen]);

  const unreadNewNotificationsCount = useMemo(
    () => newNotifications.filter((item) => item.status === 'UNREAD').length,
    [newNotifications],
  );

  const unreadViewManagerNotificationsCount = useMemo(
    () => notifications.filter((item) => !item.readStatus).length,
    [notifications],
  );

  useEffect(() => {
    setExternalNotificationCount(unreadNewNotificationsCount);
  }, [setExternalNotificationCount, unreadNewNotificationsCount]);

  const sortedViewShareLegacyNotifications = useMemo(() => {
    const unread = notifications.filter((item) => !item.readStatus);
    const read = notifications.filter((item) => item.readStatus);

    return [...unread, ...read];
  }, [notifications]);

  // Executes View Manager notification actions and applies local state updates.
  async function markNotificationAsRead({
    recipient_user_id,
    markAll = false,
    clearAll = false,
    viewmanagerID,
    rejectViewManager = false,
    acceptViewManager = false,
    readStatus = false,
  }: {
    recipient_user_id?: string | number;
    markAll?: boolean;
    clearAll?: boolean;
    viewmanagerID?: string | number;
    rejectViewManager?: boolean;
    acceptViewManager?: boolean;
    readStatus?: boolean;
  }) {
    try {
      setNotificationFetchingStarted(true);
      let response;

      if (clearAll) {
        response = await handleClearAll(Number(recipient_user_id));
      } else if (markAll) {
        response = await handleMarkAll(Number(recipient_user_id));
      } else if (rejectViewManager) {
        response = await handleRejectViewManager(Number(viewmanagerID), Number(recipient_user_id));
      } else if (acceptViewManager) {
        response = await handleAcceptViewManager(Number(viewmanagerID), Number(recipient_user_id));
      } else {
        response = await handleMarkSingle(Number(viewmanagerID), Number(recipient_user_id), !readStatus);
      }

      if (response.errors) {
        console.error('Failed to apply View Manager notification action', response.errors);
        setNotificationFetchingStarted(false);
        return { success: false, error: response.errors };
      }

      handlePostResponseUpdate(viewmanagerID, !readStatus, markAll, clearAll, rejectViewManager, acceptViewManager);

      setNotificationFetchingStarted(false);
      return { success: true, response };
    } catch (error) {
      console.error('Failed to apply View Manager notification action', error);
      setNotificationFetchingStarted(false);
      return { success: false, error: t('exception_occurred') };
    }
  }

  // Clears all View Manager notifications for the current recipient.
  const handleClearAll = async (recipient_user_id: number) => {
    return await postRequest('/api/updateNotificationStatus', {
      recipient_user_id,
      clearAll: true,
    });
  };

  // Marks all View Manager notifications as read for the current recipient.
  const handleMarkAll = async (recipient_user_id: number) => {
    return await postRequest('/api/updateNotificationStatus', {
      recipient_user_id,
      markAll: true,
    });
  };

  // Rejects a shared view notification and removes it from the View Manager list.
  const handleRejectViewManager = async (viewmanagerID: number, recipient_user_id: number) => {
    if (viewmanagerID === null) {
      throw new Error('Missing id for view manager delete');
    }
    return await postRequest('/api/updateNotificationStatus', {
      viewmanagerID,
      recipient_user_id,
      rejectViewManager: true,
    });
  };

  // Accepts a shared view notification and activates the corresponding view.
  const handleAcceptViewManager = async (viewmanagerID: number, recipient_user_id: number) => {
    if (viewmanagerID == null) {
      throw new Error('Missing viewmanagerID for acceptViewManager');
    }
    return await postRequest('/api/updateNotificationStatus', {
      viewmanagerID,
      acceptViewManager: true,
      recipient_user_id,
    });
  };

  // Toggles a single View Manager notification read state.
  const handleMarkSingle = async (viewmanagerID: number, recipient_user_id: number, readStatus: boolean) => {
    if (viewmanagerID === null) {
      throw new Error('Missing id for single notification');
    }
    return await postRequest('/api/updateNotificationStatus', {
      viewmanagerID,
      recipient_user_id,
      markAll: false,
      readStatus,
    });
  };

  // Removes one View Manager notification locally and tops up the visible list when needed.
  const removeViewManagerNotificationAndTopUp = (
    viewmanagerID: string | number,
    setAsProcessed: boolean,
  ) => {
    notifications.forEach((obj) => {
      if (obj?.notificationId === viewmanagerID) {
        updateNotificationFields(obj.notificationId, {
          readStatus: true,
        });
      }
    });

    if (setAsProcessed) {
      setProcessedNotificationId(String(viewmanagerID));
    }

    removeNotification(viewmanagerID);

    const { nextLoadedCount, nextTotalCount } = getCountsAfterRemoval(viewManagerPageState);

    setViewManagerPageState((prev) => ({
      ...prev,
      loadedCount: nextLoadedCount,
      totalCount: nextTotalCount,
    }));

    void topUpViewManagerAfterRemoval(nextLoadedCount, nextTotalCount);
  };

  // Fills one View Manager notification slot after accept/reject removes an item.
  const topUpViewManagerAfterRemoval = async (nextLoadedCount: number, nextTotalCount: number) => {
    if (nextTotalCount <= nextLoadedCount) {
      return;
    }

    await loadViewManagerNotifications({
      limit: nextLoadedCount + 1,
      showInitialLoader: false,
    });
  };

  // Applies post-response state changes for View Manager notification actions.
  const handlePostResponseUpdate = (
    viewmanagerID: string | number,
    readStatus: boolean,
    markAll: boolean,
    clearAll: boolean,
    rejectViewManager: boolean,
    acceptViewManager: boolean,
  ) => {
    updateNotificationFields(viewmanagerID, {
      readStatus,
    });

    if (markAll) {
      notifications.forEach((obj) => {
        updateNotificationFields(obj.notificationId, {
          readStatus: true,
        });
      });
    }
    if (clearAll) {
      clearAllNotifications();
    }

    if (rejectViewManager) {
      removeViewManagerNotificationAndTopUp(viewmanagerID, false);
    }

    if (acceptViewManager) {
      removeViewManagerNotificationAndTopUp(viewmanagerID, true);
    }
  };

  // Loads additional View Manager notifications when the user clicks Show more.
  const handleShowMoreViewManagerNotifications = async () => {
    if (viewManagerPageState.loadingMore || notificationFetchingStarted || !viewManagerHasMore) {
      return;
    }

    await loadViewManagerNotifications({
      limit: viewManagerPageState.loadedCount + VIEW_MANAGER_PAGE_SIZE,
      showInitialLoader: false,
    });
  };


  // Handles General notification actions and reapplies ordering after status updates.
  const handleNewNotificationAction = async (
    notification: NewNotificationItem,
    action: NewNotificationAction,
  ) => {
    if (!notification.notificationId || !notification.userNotificationId) {
      return;
    }

    const processingId = String(notification.userNotificationId);
    if (processingNewNotificationIdSet.current.has(processingId)) {
      return;
    }

    processingNewNotificationIdSet.current.add(processingId);
     setNotificationFetchingStarted(true);
    setProcessingNewNotificationIds((prev) => ({
      ...prev,
      [processingId]: true,
    }));

    try {

      const currentStatus: NotificationStatus = notification.status ?? 'UNREAD';

      if (action === 'DELETE') {
        const deleteResult = await updateNewNotificationStatus(
          notification.userNotificationId,
          currentStatus,
          'DELETED',
        );

        if (!deleteResult.success) {
          console.error('Failed to soft delete notification', deleteResult.error);
          return;
        }
        removeGeneralNotificationAndTopUp(notification.userNotificationId);
        return;
      }

      const actingUserId =
        userDetails?.userId != null ? Number(userDetails.userId) : notification.userId ?? null;

      const result = await resolveNotificationAction({
        item: {
          notificationId: notification.notificationId,
          userNotificationId: notification.userNotificationId,
          status: currentStatus,
          type: notification.type,
          payload: notification.payload,
          userId: notification.userId,
          actingUserId,
        },
        action,
      });

      if (!result.success) {
        const errorMessage = 'error' in result ? result.error : 'Unknown notification workflow error';
        console.error('Failed to process notification action', errorMessage);
        return;
      }

      if (result.status === 'ACCEPTED' || result.status === 'REJECTED' || result.status === 'DELETED') {
        removeGeneralNotificationAndTopUp(notification.userNotificationId);
        return;
      }

      const nowIso = new Date().toISOString();

      setNewNotifications((prev) => {
        const updatedItems = prev.map((item) =>
          item.userNotificationId === notification.userNotificationId
            ? {
                ...item,
                status: result.status,
                updateDate: result.status === 'READ' ? nowIso : item.updateDate,
              }
            : item,
        );

        const reorderedItems =
          result.status === 'UNREAD'
            ? moveGeneralNotificationToUnreadTop(updatedItems, notification.userNotificationId)
            : updatedItems;

        return reorderLoadedGeneralNotifications(reorderedItems);
      });
    } finally {
      setNotificationFetchingStarted(false);
      processingNewNotificationIdSet.current.delete(processingId);
      setProcessingNewNotificationIds((prev) => ({
        ...prev,
        [processingId]: false,
      }));
    }
  };

  // Marks all General unread notifications as read and reorders loaded items locally.
  const handleMarkAllGeneralAsRead = async () => {
    const userId = userDetails?.userId != null ? Number(userDetails.userId) : null;
    if (!userId) {
      return;
    }

    setNewNotificationsLoading(true);
    try {
      const result = await markAllNotificationsAsRead(userId);
      if (!result.success) {
        console.error('Failed to mark all General notifications as read', result.error);
        return;
      }

      setNewNotifications((prev) =>
        reorderLoadedGeneralNotifications(
          prev.map((item) =>
            item.status === 'UNREAD'
              ? { ...item, status: 'READ', updateDate: new Date().toISOString() }
              : item,
          ),
        ),
      );
    } catch (error) {
      console.error('Failed to mark all General notifications as read', error);
    } finally {
      setNewNotificationsLoading(false);
    }
  };

  // Loads the next General notifications page when the user clicks Show more.
  const handleShowMoreGeneralNotifications = async () => {
    if (generalPageState.loadingMore || newNotificationsLoading || !generalPageState.hasMore) {
      return;
    }

    await loadGeneralNotifications({
      offset: generalPageState.offset,
      append: true,
      showInitialLoader: false,
    });
  };

  // Formats notification timestamps for card footer display.
  const formatDateTime = (time): string => {
    if (!time) return '-';

    const date = new Date(time);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const tabConfigs = [
    {
      id: 'view-manager',
      label: 'View Manager',
      unreadCount: unreadViewManagerNotificationsCount,
      onMarkAllAsRead: () =>
        markNotificationAsRead({
          recipient_user_id: userDetails.userId,
          markAll: true,
          clearAll: false,
          viewmanagerID: null,
          rejectViewManager: false,
          acceptViewManager: false,
        }),
      content: (
        <>
          {!notificationFetchingStarted && notifications.length === 0 && <EmptyNotificaion />}
          <div className={styles.tabPanelContent}>
            <div className={styles.notificationList}>
              {sortedViewShareLegacyNotifications
                .slice(0, viewManagerPageState.loadedCount)
                .map((item, idx) => {
                const viewShareNotificationCreatorName = getViewShareNotificationCreatorName(item);

                return (
                  <div key={idx}>
                    <Card
                      key={idx}
                      className={`${styles.itemCard} ${item.readStatus ? styles.noBar : styles.unread}`}
                    >
                      <div className={styles.itemInner}>
                        <div className={styles.itemHeader}>
                          <div className={styles.itemTitle}>{getLocalizedViewManagerLabel(item, t, language)}</div>
                          <div className={styles.headerIcons}>
                            {item.readStatus === true ? (
                              <div
                                onClick={() =>
                                  markNotificationAsRead({
                                    recipient_user_id: userDetails.userId,
                                    markAll: false,
                                    clearAll: false,
                                    viewmanagerID: item?.notificationId,
                                    rejectViewManager: false,
                                    acceptViewManager: false,
                                    readStatus: item.readStatus,
                                  })
                                }
                              >
                                <DraftsOutlinedIcon
                                  className={styles.readicon}
                                  data-testid='read-notification-icon'
                                />
                              </div>
                            ) : (
                              <div
                                onClick={() =>
                                  markNotificationAsRead({
                                    recipient_user_id: userDetails.userId,
                                    markAll: false,
                                    clearAll: false,
                                    viewmanagerID: item?.notificationId,
                                    rejectViewManager: false,
                                    acceptViewManager: false,
                                    readStatus: item.readStatus,
                                  })
                                }
                              >
                                <MarkunreadOutlinedIcon className={styles.readicon} />
                              </div>
                            )}
                          </div>
                        </div>

                        {item.description && <div className={styles.itemDesc}>{item.description}</div>}

                        <div className={styles.itemFooter}>
                          <span className={styles.timestamp}>
                            <span className={styles.createdByText}>{viewShareNotificationCreatorName}</span>
                            {' | '}
                            {formatDateTime(item.date)}
                          </span>
                          <div className={styles.actions}>
                            <Button
                              type='button'
                              variant='ghost'
                              size='small'
                              className={styles.accept}
                              onClick={() =>
                                markNotificationAsRead({
                                  recipient_user_id: userDetails.userId,
                                  markAll: false,
                                  clearAll: false,
                                  viewmanagerID: item?.notificationId,
                                  rejectViewManager: false,
                                  acceptViewManager: true,
                                })
                              }
                            >
                              {t('notification_panel.accept')}
                            </Button>
                            <Button
                              type='button'
                              variant='ghost'
                              size='small'
                              className={styles.reject}
                              onClick={() =>
                                markNotificationAsRead({
                                  recipient_user_id: userDetails.userId,
                                  markAll: false,
                                  clearAll: false,
                                  viewmanagerID: item?.notificationId,
                                  rejectViewManager: true,
                                  acceptViewManager: false,
                                })
                              }
                            >
                              {t('notification_panel.reject')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
                })}
              <ShowMoreControl
                visible={viewManagerHasMore}
                loading={viewManagerPageState.loadingMore}
                disabled={viewManagerPageState.loadingMore || notificationFetchingStarted}
                onClick={handleShowMoreViewManagerNotifications}
                remainingCount={viewManagerRemainingCount}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      id: 'general',
      label: 'General',
      unreadCount: unreadNewNotificationsCount,
      onMarkAllAsRead: handleMarkAllGeneralAsRead,
      content: (
        <>
          {!newNotificationsLoading && newNotifications.length === 0 && <EmptyNotificaion />}
          <div className={styles.tabPanelContent}>
            <div className={styles.notificationList}>
              {newNotifications.map((item) => (
                <NewNotificationCard
                  key={item.userNotificationId}
                  item={item}
                  isProcessing={Boolean(processingNewNotificationIds[String(item.userNotificationId)])}
                  disableReadAction={item.status === 'ACCEPTED' || item.status === 'REJECTED'}
                  onAction={(action) => handleNewNotificationAction(item, action)}
                  formatDateTime={formatDateTime}
                />
              ))}
              <ShowMoreControl
                visible={generalPageState.hasMore}
                loading={generalPageState.loadingMore}
                disabled={generalPageState.loadingMore || newNotificationsLoading}
                onClick={handleShowMoreGeneralNotifications}
                remainingCount={generalRemainingCount}
              />
            </div>
          </div>
        </>
      ),
    },
  ];

  const activeTabConfig = tabConfigs[selectedTab];

  return (
    <div>
      <div
        className={styles.container}
        style={{
          pointerEvents: notificationFetchingStarted ? 'none' : 'auto',
          opacity: notificationFetchingStarted ? 0.5 : 1,
        }}
      >
        <Tabs
          aria-label='Notification categories'
          value={selectedTab}
          onChange={(_event, value) => setSelectedTab(Number(value))}
        >
          {tabConfigs.map((tab, idx) => (
            <Tab key={tab.id} value={idx}>
              {tab.label}
            </Tab>
          ))}
        </Tabs>
        <div className={styles.unreadRow}>
          <label>
            {activeTabConfig?.unreadCount ?? 0} {t('notification_panel.unread')}
          </label>
          <div className={styles.unreadActions}>
            <Button
              size='xsmall'
              type='button'
              variant='primary'
              disabled={(activeTabConfig?.unreadCount ?? 0) === 0}
              onClick={() => activeTabConfig?.onMarkAllAsRead()}
            >
              {t('notification_panel.mark_all_as_read')}
            </Button>
            {clearAllVisible && (
              <Button
                size='xsmall'
                type='button'
                variant='error'
                disabled={notifications.length === 0}
                onClick={() =>
                  markNotificationAsRead({
                    recipient_user_id: userDetails.userId,
                    markAll: false,
                    clearAll: true,
                    viewmanagerID: null,
                    rejectViewManager: false,
                    acceptViewManager: false,
                  })
                }
              >
                {t('notification_panel.clear_all')}
              </Button>
            )}
          </div>
        </div>
        {tabConfigs[selectedTab]?.content}
      </div>
      {notificationFetchingStarted && (
        <div className={styles.spinnerOverlay}>
          <Spinners />
        </div>
      )}
    </div>
  );
}

export function EmptyNotificaion() {
  const { setNotificationPanelOpen } = useNotificationContext();
  const { t } = useLanguageContextAPI();
  return (
    <>
      <div className={styles.emptyState}>
        <div className={styles.emptyInner}>
          <div className={styles.emptyIllustration}>
            <MarkEmailReadOutlined className={styles.iconImage} sx={{ fontSize: 48 }} />
          </div>

          <h2 className={styles.emptyTitle}> {t('notification_panel.youre_all_caught_up')}</h2>
          <p className={styles.emptySub}> {t('notification_panel.no_new_notifications')}</p>

          <button
            className={styles.primaryButton}
            type='button'
            onClick={() => setNotificationPanelOpen(false)}
          >
            {t('notification_panel.close')}
          </button>
        </div>
      </div>
    </>
  );
}
